import { css } from '@emotion/react';

export const breakpoints = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
};

const media = {
  sm: (...args) => css`
    @media (min-width: ${breakpoints.sm}px) {
      ${css(...args)}
    }
  `,
  md: (...args) => css`
    @media (min-width: ${breakpoints.md}px) {
      ${css(...args)}
    }
  `,
  lg: (...args) => css`
    @media (min-width: ${breakpoints.lg}px) {
      ${css(...args)}
    }
  `,
  xl: (...args) => css`
    @media (min-width: ${breakpoints.xl}px) {
      ${css(...args)}
    }
  `,
};

const defaultTheme = {
  breakpoints: Object.values(breakpoints).map((b) => `${b}px`),

  media,

  fonts: {
    serif: 'morion, serif;',
    sans: 'europa, sans-serif;',
    variable: 'morion, serif;',
  },

  fontWeights: {
    bold: '700',
    normal: '400',
    light: '300',

    heading: 'inherit',
    headingLight: '200',
    headingNormal: '400',
    headingBold: '600',
  },

  fontSizes: {
    // Text sizes
    extraSmall: '12px',
    tiny: '14px',
    small: '16px',
    body: '18px',
    large: '26px',

    // Heading sizes
    h7M: '16px',
    h6M: '18px',
    h5M: '16px',
    h4M: '24px',
    h3M: '22px',
    h2M: '24px',
    h1M: '32px',
    heroM: '50px',
    menuM: '28px',

    h7: '16px',
    h6: '18px',
    h5: '18px',
    h4: '24px',
    h3: '28px',
    h2: '36px',
    h1: '40px',
    hero: '100px',
    menu: '42px',
  },

  letterSpacing: {
    tiny: '0.05em',
    small: '0.1em',
    large: '0.15em',
  },

  space: Array(33)
    .fill(0)
    .map((_, i) => i * 8),

  colors: {
    textWhite: 'white',
    textBlack: '#1F1F1F',
    textDark: '#484848',
    textLight: '#6E6E6E',

    bgLight: '#F1EDE6',
    bgLighter: '#F4F1EC',

    borderDark: '#E0DED9',

    baseWhite: 'white',
    baseBlack: 'black',
    baseGray: '#969696',
    baseGrayLight: '#CECCC7',
    baseGrayVeryLight: '#E8E9EB',
    baseGrayDark: '#484848',

    // TODO: Change to brandPrimary to be more abstract - this color might change to something that could not be described as "brown"
    brandBrown: '#cb9275',
    // TODO: The green is a lodge color for Sweni Lodge. Should be changed to constant for the lodge
    brandGreen: '#44833e',
    brandBeige: '#f4f1ed',
    transparent: 'transparent',
    statusError: '#BA3139',
  },

  speed: {
    default: '0.3s',
    slow: '0.5s',
    fast: '0.1s',
    slowest: '5s',
  },

  opacity: {
    full: 1,
    medium: 0.7,
    feint: 0.3,
    opaque: 0,
  },

  zIndex: {
    negative: '-1',
    level: '0',
    default: '1',
    filterArrows: '5',
    pattern: '9',
    actionBar: '10',
    notification: '999',
    navbar: '1000',
    modal: '1060', // set by gatsby-plugin-modal-routing
    modalContent: '1061',
    videoModal: '1062',
    planYourTripModal: '1063',
    dropdown: '1000',
    tooltip: '10000',
    cookiebanner: '70001', // Set by cookiehub banner
    pageTransitionMask: '10000',
  },

  icons: {
    accommodation: 'accommodation-duo',
    rates: 'rates-duo',
    times: 'checkin-duo',
    children: 'children-duo',
    'map-directions': 'directions-duo',
    'weather-packing': 'weather-duo',
    'health-travel': 'health-duo',
    'contact-info': 'contact-duo',
    'jet-service': 'jet-duo',
    'air-access': 'airaccess-duo',
    vimeo: 'vimeo',
    twitter: 'twitter',
    facebook: 'facebook',
    instagram: 'instagram',
    linkedin: 'linkedin',
    youtube: 'youtube',
    wikipedia: 'wiki',
    spotify: 'spotify',
    podcast: 'podcast',
  },

  pattern:
    'M85.2682655 139.319715v12.727922h-1v-12.727922h1zm-17.5503528-22.445092l.0070716 4.999952v30.165175h-1l5e-7-30.164468-.0070711-4.999245.999999-.001414zm-5.492522 16.952211l.0000556 1-12.7130728.000707-.0000556-1 12.7130728-.000707zm17.5440542-.00707l4.9979014.007071 5.0005884-.006365h12.7279223v1H89.7685711l-5.0012956.006364-4.9985378-.007071h-7.5377583v-1l7.5384655.000001zm5.5029925-11.937797v7.438763h-1v-7.438763h1zM35.9707441 90.0572993l-.000262 26.3698537 26.3844619.000204v1H34.9707441V90.0572993h1zm81.2300179-17.7269442v44.9203169l-27.3778359.007202-4.9978306-.007071-5.0004207.007071-7.5448294-.007071.0009372-1 7.5436536.007072 5.0006593-.007071 4.9984062.00707 26.3772223-.006557.000038-43.9209619h1zm-5.609837 12.2939135l.000066 1.0000658-26.3703623-.0008671-.0068326 26.3780617-.9999999-.000263.0072026-27.3769974h27.3699262zm-76.3693861-.1770573l4.7917319.0060455.0067701-.006046h27.5771645v27.3700572h-1l-.0002552-26.3705261H40.4339993l-.0071089.007833-5.2059879-.0073641-4.784686.0002382-.0068356.0075597-7.646605-.0007268.000095-1 7.2326171-.000318.0067408-.0060167 5.1993101-.0007359zm99.3671421-11.9851494l.000707 30.2648771h-1l-.000707-30.2648771h1zM18.4046725 49.7434282l-6e-7 12.7145579-.007778 4.9970526.0070713 5.0004208-.0070713 7.5436535.0070711 5.0005886-.0063635 4.9993156v12.7137798h-1l4e-7-12.7144162.006364-4.9986084-.0070713-5.0004208.0070713-7.5436535-.0070709-5.0007302.0077775-4.9977598V49.7434282h1zm-5.4999091 34.7626373l.0000556 1-12.7130728.0007071-.00005563-1 12.71307283-.0007071zm138.9116496-.0065761v1h-12.727922v-1h12.727922zm-22.722381-.0003535v1h-7.438763v-1h7.438763zM35.8455317 72.4542633l.0077782 7.5455365-.9999994.0010308-.0077782-7.5455365.9999994-.0010308zm49.5478162-49.2606611L85.386839 67.006819l26.384345.0010859 4.998538.0070705 12.443735-.0007067 5.000589-.0063643h17.720096v1l-17.71946-4e-7-5.000541.0063643-12.445127.0007066-4.998537-.0070705h-27.38428l.0071509-44.8144618 1 .0001596zM12.905074 66.9565237v1H.19129412v-1H12.905074zm54.8756178-43.9472676l.0007071 44.8222017-44.8222076-.0007203.0000855-1.0000095 43.8217309-.0007158-.0003159-43.8206993 1-.0000568zm-5.6092477 12.1955317l.0000658 1.0000658-26.3710691-.0008942-.0061255 26.3780887-.9999999-.0002631.0072025-27.3769972h27.3699262zM134.588681 49.73565v12.727922h-1V49.73565h1zm-17.564704-14.7152378v27.3843313h-1l-.000365-26.3845549-26.3766982.007492-.0001973-1.0000658 27.3772605-.0072026zm-44.7953813.0518185l7.5455365.0077782-.0010309.9999995-7.5455364-.0077782.0010308-.9999995zm7.5408394-17.4442493l4.9979013.007071 5.0005885-.0063644h12.7137801v1l-12.7131437-4e-7-5.0012957.0063639-4.9980691-.0070708-7.5460052.0070708-4.9985377-.0070706-17.7052189.0007071-.0000557-1 17.7059818-.0007066 4.9980691.0070708 7.5460052-.0070708zM67.7252998.42251804V13.136298h-1V.42251804h1zM85.261053.40802235V13.1359444h-1V.40802235h1z',
};

const spvcTheme = {
  ...defaultTheme,
  colors: {
    ...defaultTheme.colors,
    baseGrayDark: '#AAABAB',
    textBlack: '#ECECEC',
    textDark: '#AAABAB',
    brandBrown: '#cb9275',
    bgLight: '#212423',
    bgLighter: '#353535',
    borderDark: '#484848',
    baseGrayVeryLight: '#484848',
  },
  fonts: {
    ...defaultTheme.fonts,
    variable: 'europa, sans-serif;',
  },
  fontSizes: {
    ...defaultTheme.fontSizes,
    hero: '80px',
  },
};

const theme = process.env.GATSBY_SITE_ID === 'spvc' ? spvcTheme : defaultTheme;

export default theme;

/** Types */
export type TextFontSize =
  | 'extraSmall'
  | 'caption'
  | 'tiny'
  | 'small'
  | 'body'
  | 'large';
export type TextFontWeight = 'light' | 'normal' | 'bold';
export type HeadingFontSize =
  | 'hero'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'h7'
  | 'menu';
export type HeadingFontWeight = 'light' | 'normal' | 'bold';
